/**
 * Atualiza o título da aba do browser com o código da rotina em evidência
 * (ex.: `B321 · Harpia WMS`, `1150 · Harpia WMS`).
 *
 * Fontes (nesta ordem):
 * 1. Rótulo da aba desktop ativa (`1150 — Cadastro…`)
 * 2. Path da rota (`/gestaoAcessos/1150`, `/PROC_…`)
 * 3. `userStore.currentPageName` (coletor)
 */

import {
	CODIGO_POR_FORMULARIO,
	codigoDaRotina,
} from "~/composables/menus/iconesRotinas";
import { useDesktopTabsStore } from "~/store/tabs/desktopTabs.store";

const APP_NAME = "Harpia WMS";

/**
 * Código no início do rótulo da aba/menu: `1150 — …`, `B321 - …`, `34A — …`.
 */
function codigoDoRotulo(label?: string | null): string | null {
	if (!label) return null;
	const t = label.trim();
	const m = t.match(/^([A-Za-z]{0,3}\d{1,4}[A-Za-z0-9]*)\s*[—–\-]/);
	if (m?.[1]) return m[1].toUpperCase();
	if (/^[A-Za-z]{0,3}\d{1,4}[A-Za-z0-9]*$/i.test(t)) return t.toUpperCase();
	return null;
}

/**
 * Código a partir do path da rota.
 */
function codigoDoPath(path?: string | null): string | null {
	if (!path || path === "/") return null;
	const parts = path
		.split("/")
		.filter(Boolean)
		.map(p => decodeURIComponent(p));

	for (let i = parts.length - 1; i >= 0; i--) {
		const seg = parts[i]!;

		// Nome de formulário Delphi → código curto
		if (CODIGO_POR_FORMULARIO[seg]) {
			return CODIGO_POR_FORMULARIO[seg];
		}
		const viaMapa = codigoDaRotina(seg);
		if (viaMapa && viaMapa !== seg && !seg.startsWith("PROC_")) {
			return viaMapa.toUpperCase();
		}

		// Segmento já é código: 1150, B321, 34A, B32B
		if (/^[A-Za-z]{0,3}\d{1,4}[A-Za-z0-9]*$/i.test(seg)) {
			return seg.toUpperCase();
		}
	}

	// Último segmento PROC_* sem mapeamento: tenta mapa; senão omite (muito longo)
	const last = parts[parts.length - 1];
	if (last && CODIGO_POR_FORMULARIO[last]) {
		return CODIGO_POR_FORMULARIO[last];
	}
	return null;
}

/**
 * Ativa o watcher do título da aba. Chamar uma vez em `app.vue`.
 */
export function useDocumentTitleRotina() {
	const route = useRoute();
	const tabsStore = useDesktopTabsStore();
	const userStore = useUserStore();

	const tituloAba = computed(() => {
		const path = route.path || "/";

		if (path === "/login" || /\/login$/i.test(path)) {
			return `Login · ${APP_NAME}`;
		}

		if (path === "/") {
			return APP_NAME;
		}

		const pathNorm =
			typeof tabsStore.normalizarPath === "function"
				? tabsStore.normalizarPath(path)
				: path;

		const tabAtiva =
			tabsStore.tabs.find(t => t.path === tabsStore.activePath) ||
			tabsStore.tabs.find(t => t.path === pathNorm);

		const codigo =
			codigoDoRotulo(tabAtiva?.label) ||
			codigoDoPath(pathNorm) ||
			codigoDoPath(tabsStore.activePath) ||
			codigoDoRotulo(userStore.currentPageName);

		if (codigo) {
			return `${codigo} · ${APP_NAME}`;
		}

		// Fallback: rótulo da aba (sem código reconhecido)
		if (tabAtiva?.label?.trim()) {
			return `${tabAtiva.label.trim()} · ${APP_NAME}`;
		}

		return APP_NAME;
	});

	useHead({
		title: tituloAba,
	});

	return { tituloAba };
}
